import { Component } from '@theme/component';

// "region" = handleized surface_name ("shaft", "front", …) — canonical runtime
// identifier across JS state, event detail, DOM data-position, hidden input
// keys. "surface_N" is Shopify metafield storage only.

// Canonical source: `pour_tier` + `rarity_tier` metaobjects, emitted as a JSON
// blob by swatch-picker-impl.liquid. These hardcoded fallbacks mirror current
// Liquid values for partial-deploy safety; loadPourCostMap() overrides on mount.
let COST_TO_POUR_STYLE = {
  1: 'Monolithic',
  2: 'Dichotomic',
  3: 'Triadic',
  4: 'Sigilic',
  5: 'Aetheric',
  6: 'Hexic',
};

let COST_TO_RARITY = {
  1: 'Common',
  2: 'Uncommon',
  3: 'Rare',
  4: 'Epic',
  5: 'Legendary',
  6: 'Mythic',
};

let costMapLoaded = false;
function loadPourCostMap() {
  if (costMapLoaded) return;
  costMapLoaded = true;
  const tag = document.querySelector('script[type="application/json"][data-pour-cost-map]');
  if (!tag) return;
  try {
    const data = JSON.parse(tag.textContent || '{}');
    if (data?.pourStyleToCost) {
      const next = {};
      for (const [name, cost] of Object.entries(data.pourStyleToCost)) {
        next[cost] = name;
      }
      COST_TO_POUR_STYLE = next;
    }
    if (data?.costToRarity) {
      // Coerce JSON's string keys to numbers; call sites read with numeric cost.
      const next = {};
      for (const [costStr, name] of Object.entries(data.costToRarity)) {
        next[Number(costStr)] = name;
      }
      COST_TO_RARITY = next;
    }
  } catch (err) {
    console.warn('[swatch-picker] failed to parse pour cost map — falling back to hardcoded constants', err);
  }
}

// "shaft:319145345389,base:319058968941" → [[position, swatchId], …].
// Handles 2-pair (mainline) and future N-pair combos uniformly.
function parseAppliesTo(s) {
  if (!s) return [];
  return s.split(',').map((pair) => {
    const idx = pair.indexOf(':');
    if (idx === -1) return null;
    const key = pair.substring(0, idx).trim();
    const id = pair.substring(idx + 1).trim();
    if (!key || !id) return null;
    return [key, id];
  }).filter(Boolean);
}

// Chip sort: [cost, hue, saturation, lightness]. Cost matches Liquid's pre-sort;
// HSL is the perceptual secondary sort Liquid can't compute. Achromatic swatches
// (s === 0) get hue=361 so they cluster after chromatic ones, then ramp by lightness.
function chipSortKey(chip) {
  const cost = Number(chip.dataset.colorCost) || 0;
  const m = /^#?([0-9a-f]{6})$/i.exec((chip.dataset.color || '').trim());
  if (!m) return [cost, 361, 0, 0];
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [cost, 361, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d) + (g < b ? 6 : 0);
  else if (max === g) h = ((b - r) / d) + 2;
  else h = ((r - g) / d) + 4;
  return [cost, h * 60, s, l];
}

// Shared compare over chipSortKey — used by the region grids (#sortChips)
// and the custom builder's solid palette, so both surfaces read in the same
// perceptual order.
function compareChipOrder(a, b) {
  const ka = chipSortKey(a);
  const kb = chipSortKey(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return ka[i] - kb[i];
  }
  return 0;
}

// Floor so the thumb stays grabbable on a long grid.
const MIN_THUMB_HEIGHT = 28;

// Scroll rail for one chip area, standing in for the native scrollbar that
// swatch-picker.css hides. Customers weren't reading the cropped final row as
// "there's more here", and a native bar doesn't fix it on touch — iOS/Android
// paint nothing until a swipe is already underway. Because the native bar is
// gone, drag and track-click are implemented here too.
class ChipScrollbar {
  #area;
  #rail;
  #thumb;
  #frame = null;
  // Active drag pointer; null when idle.
  #pointerId = null;
  #dragOriginY = 0;
  #dragOriginScrollTop = 0;
  #resizeObserver = new ResizeObserver(() => this.#schedule());

  // Optional hook run before each measurement. Returns false to park the rail
  // (used by the mobile drawer, which is only a scroller while it's open).
  #prepare;

  constructor(area, rail, prepare = null) {
    this.#area = area;
    this.#rail = rail;
    this.#prepare = prepare;
    this.#thumb = rail.querySelector('.swatch-picker__scrollbar-thumb');
    if (!this.#thumb) return;
    area.addEventListener('scroll', this.#schedule, { passive: true });
    // pointerdown on the thumb bubbles to the rail, so one listener covers
    // both the drag and the track-click.
    rail.addEventListener('pointerdown', this.#onPointerDown);
    // Capture is taken on the thumb, so the move/up pair lands there even when
    // the pointer leaves the 6px rail mid-drag.
    this.#thumb.addEventListener('pointermove', this.#onPointerMove);
    this.#thumb.addEventListener('pointerup', this.#onPointerUp);
    this.#thumb.addEventListener('pointercancel', this.#onPointerUp);
    // Catches the region/tab panels unhiding (0 → real height) and viewport
    // resizes. It can NOT see a category swap — same box, different content —
    // which is why the picker calls update() there.
    this.#resizeObserver.observe(area);
    this.update();
  }

  // Called by the picker whenever the visible grid changes under a fixed box.
  update() {
    if (!this.#thumb) return;
    if (this.#prepare && !this.#prepare()) {
      this.#rail.dataset.scrollable = 'false';
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = this.#area;
    const overflow = scrollHeight - clientHeight;
    // 1px slack — sub-pixel layout leaves a hair of overflow on grids that fit.
    const scrollable = overflow > 1;
    this.#rail.dataset.scrollable = String(scrollable);
    // Hidden panels measure 0 here; the ResizeObserver re-fires on unhide.
    if (!scrollable) return;
    const trackHeight = this.#rail.clientHeight;
    const thumbHeight = Math.min(
      trackHeight,
      Math.max(MIN_THUMB_HEIGHT, Math.round((trackHeight * clientHeight) / scrollHeight))
    );
    this.#thumb.style.height = `${thumbHeight}px`;
    const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
    this.#thumb.style.transform = `translateY(${Math.round((scrollTop / overflow) * maxThumbTop)}px)`;
  }

  destroy() {
    this.#resizeObserver.disconnect();
    this.#area.removeEventListener('scroll', this.#schedule);
    this.#rail.removeEventListener('pointerdown', this.#onPointerDown);
    this.#thumb?.removeEventListener('pointermove', this.#onPointerMove);
    this.#thumb?.removeEventListener('pointerup', this.#onPointerUp);
    this.#thumb?.removeEventListener('pointercancel', this.#onPointerUp);
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  // Throttle to one write per frame — scroll fires far faster than paint.
  #schedule = () => {
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.update();
    });
  };

  #maxThumbTop() {
    return Math.max(0, this.#rail.clientHeight - this.#thumb.offsetHeight);
  }

  // Position the thumb's TOP edge at `top` px down the track.
  #scrollToThumbTop(top) {
    const maxThumbTop = this.#maxThumbTop();
    if (maxThumbTop <= 0) return;
    const { scrollHeight, clientHeight } = this.#area;
    const ratio = Math.min(1, Math.max(0, top / maxThumbTop));
    this.#area.scrollTop = ratio * (scrollHeight - clientHeight);
  }

  #onPointerDown = (event) => {
    if (this.#rail.dataset.scrollable !== 'true') return;
    if (!this.#thumb.contains(event.target)) {
      // Track click. Native bars page a screen at a time; centring the thumb on
      // the click reads better on a 3-row grid, and leaves the pointer on the
      // thumb so the press can continue straight into a drag.
      this.#scrollToThumbTop(
        event.clientY - this.#rail.getBoundingClientRect().top - this.#thumb.offsetHeight / 2
      );
    }
    this.#pointerId = event.pointerId;
    this.#dragOriginY = event.clientY;
    // Read back: scrollTop is clamped on assignment, and a track click above
    // may have just moved it.
    this.#dragOriginScrollTop = this.#area.scrollTop;
    this.#rail.dataset.dragging = 'true';
    this.#thumb.setPointerCapture(event.pointerId);
    // Suppress the text-selection drag.
    event.preventDefault();
  };

  #onPointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    const maxThumbTop = this.#maxThumbTop();
    if (maxThumbTop <= 0) return;
    const { scrollHeight, clientHeight } = this.#area;
    // Thumb travel → scroll travel, so the grid tracks the cursor 1:1.
    const delta = event.clientY - this.#dragOriginY;
    this.#area.scrollTop =
      this.#dragOriginScrollTop + (delta / maxThumbTop) * (scrollHeight - clientHeight);
  };

  #onPointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    this.#pointerId = null;
    delete this.#rail.dataset.dragging;
    if (this.#thumb.hasPointerCapture(event.pointerId)) {
      this.#thumb.releasePointerCapture(event.pointerId);
    }
  };
}

class SwatchPicker extends Component {
  // Sparkle picks keyed by `data-section-id` — which is actually the picker
  // SCOPE id (PDP uses section.id; configurator uses section.id-block.id per
  // panel). Static cache so a remount restores customer picks before the
  // default cascade runs.
  static #sparkleCache = new Map();

  #mode = 'split';
  #activeRegion = null;
  // 'signatures', 'custom', or 'premades'. Tab strip renders union-of-variants,
  // so customer choice survives variant:update.
  #activeTab = 'custom';
  // Per-variant surface map built by #initSurfaces from rendered region tabs.
  // Shape: { [region]: { propertyKey, handleKey, sparkleKey, surfaceName, isModel } }.
  // isModel = true iff this is the synthetic Entire-mode paint-all surface.
  #positions = {};
  #modelKey = null;
  #splitKeys = [];
  // Entire: [#modelKey] (or []); Split: #splitKeys.
  #modeRegions = { entire: [], split: [] };
  #selections = {};
  #typeSelections = {};
  #categorySelections = {};
  // Per-region "Add Sparkle" — orthogonal to swatch + budget; sidecar flag
  // on bunny:swatch-change so the 3D viewer drives the sparkle shader.
  #sparkleSelections = {};
  // Suppresses "manual pick clears active signature" while applying a signature
  // (which routes through chip.click() → selectSwatch).
  #applyingSignature = false;
  // Suppresses Shaft→Base auto-advance during the default cascade.
  #applyingDefaults = false;
  // Snapshot per mode so toggling Entire ↔ Split restores prior picks.
  #modeState = { entire: null, split: null };
  // Per-variant gating from a Liquid-emitted JSON blob. Entry shape:
  //   { surfaces: {[region]: ["solid","faded",…]},
  //     signatures: ["aetheric-blue", …],
  //     pour_style_costs: [1,2,3,4],   // tiers that EXIST (sparse-matrix), not in-stock
  //     available: true }              // inventory — gates the ATC button only
  #variantGating = new Map();
  #currentGating = null;
  // Map<position, Map<swatchType, Set<category>>> — built once from the chip
  // set so #filterCategories is O(1) per Type change.
  #categoryIndex = new Map();

  #onVariantUpdateBound = null;
  // The pour-configurator panel this picker lives in (null on PDP). Used to
  // filter out sibling-panel variant:update events.
  #hostPanel = null;
  // Section / panel root we listen on. Mirrors bunny-3d-viewer's scoping so
  // a sibling section's variant:update never reaches this picker (was
  // `document` before, with a #hostPanel filter inside the handler).
  #scopeRoot = null;
  // chip → position index built once per #initialize; keeps #chipsFor O(1).
  #chipsByPosition = new Map();
  // position → ChipScrollbar for that region's chip grid.
  #chipScrollbars = new Map();
  // Rail for the mobile bottom-sheet drawer, where THIS element is the scroller.
  #drawerScrollbar = null;
  #drawerClassObserver = null;
  // "Pick your own" builder (swatch-picker-custom-dialog.liquid): one
  // <dialog-component> per picker, shared by every region's Custom chips.
  #customDialog = null;
  #customLabels = { types: {}, strip: 'Custom', emptySlot: 'Pick', slotFallback: 'Colour {n}' };
  // Live builder session: { position, type, count, costOptions, slots, active }.
  // Null outside an open dialog (and after Apply).
  #customState = null;
  // True only around #applyCustomSelection's internal chip.click(), so that
  // click selects instead of reopening the builder.
  #applyingCustom = false;
  #onCustomDialogClickBound = null;

  connectedCallback() {
    super.connectedCallback();
    loadPourCostMap();
    this.#hostPanel = this.closest('[data-pour-panel]');
    this.#scopeRoot = this.#hostPanel || this.closest('.shopify-section') || document;
    this.#parseVariantGating();
    this.#initSurfaces();
    this.#bindHiddenInputsToForm();
    // Apply initial gating BEFORE the default cascade so #autoSelectDefaults
    // picks chips that are actually buyable on this variant.
    const initialGating = this.#variantGating.get(this.dataset.variantId || '');
    if (initialGating) this.#applyVariantGating(initialGating);
    this.#initialize();
    // After #initialize — the default cascade picks a category, so the grids
    // are in their resting state by the time the rails first measure.
    this.#initChipScrollbars();
    this.#initCustomDialog();
    // Render-once / gating-driven: on variant:update we don't re-render or
    // morph, just toggle chip + signature availability against the new entry.
    this.#onVariantUpdateBound = (event) => {
      if (this.#hostPanel && event.target?.closest?.('[data-pour-panel]') !== this.#hostPanel) return;
      this.#syncAfterVariantUpdate(event);
    };
    this.#scopeRoot.addEventListener('variant:update', this.#onVariantUpdateBound);
  }

  disconnectedCallback() {
    if (this.#onVariantUpdateBound) {
      (this.#scopeRoot || document).removeEventListener('variant:update', this.#onVariantUpdateBound);
      this.#onVariantUpdateBound = null;
    }
    for (const scrollbar of this.#chipScrollbars.values()) scrollbar.destroy();
    this.#chipScrollbars.clear();
    this.#drawerScrollbar?.destroy();
    this.#drawerScrollbar = null;
    this.#drawerClassObserver?.disconnect();
    this.#drawerClassObserver = null;
    if (this.#customDialog && this.#onCustomDialogClickBound) {
      this.#customDialog.removeEventListener('click', this.#onCustomDialogClickBound);
    }
    this.#customDialog = null;
    this.#onCustomDialogClickBound = null;
    super.disconnectedCallback?.();
  }

  // One rail per region's chip area, paired by data-position — plus one for
  // the mobile drawer, where the picker element itself is the scroller.
  #initChipScrollbars() {
    for (const area of this.#refList('chipAreas')) {
      const position = area.dataset.position;
      const rail = this.#refByPosition('chipScrollbars', position);
      if (!rail) continue;
      this.#chipScrollbars.set(position, new ChipScrollbar(area, rail));
    }

    const drawerRail = this.refs.drawerScrollbar;
    if (!drawerRail) return;
    this.#drawerScrollbar = new ChipScrollbar(this, drawerRail, () => {
      // Only a scroller while the bar JS has us lifted into the bottom sheet.
      if (!this.classList.contains('bunny-mobile-drawer-active')) return false;
      const rect = this.getBoundingClientRect();
      // Clear the sticky chrome strip so the thumb can't hide behind the title.
      const chrome = this.querySelector('.bunny-mobile-pdp-bar__chrome');
      const chromeHeight = chrome ? chrome.getBoundingClientRect().height : 0;
      drawerRail.style.top = `${rect.top + chromeHeight}px`;
      drawerRail.style.height = `${Math.max(0, rect.height - chromeHeight)}px`;
      return true;
    });
    // The drawer opens by class change, which resizes us — but a close/reopen
    // can land on identical dimensions, so watch the attribute directly.
    this.#drawerClassObserver = new MutationObserver(() => this.#drawerScrollbar?.update());
    this.#drawerClassObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  // Refs from Horizon's `ref="name[]"` Liquid syntax can land as either a
  // single Element (one match) or an Array (many matches). Use this anywhere
  // you'd otherwise repeat `Array.isArray(r) ? r : (r ? [r] : [])`.
  #refList(name) {
    const r = this.refs[name];
    return Array.isArray(r) ? r : (r ? [r] : []);
  }

  #parseVariantGating() {
    this.#variantGating = new Map();
    const script = this.querySelector('script[type="application/json"][data-variant-gating]');
    if (!script) return;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      for (const [id, gating] of Object.entries(parsed)) {
        this.#variantGating.set(String(id), gating);
      }
    } catch (err) {
      console.warn('[swatch-picker] failed to parse variant gating JSON:', err);
    }
  }

  // Render-once picker: on variant change, look up the new gating entry and
  // toggle chip + signature availability. Re-cascade defaults ONLY when the
  // customer's picks don't map to an EXISTING tier on the new variant —
  // #isCurrentSelectionValid checks total ∈ pour_style_costs (existence-based),
  // which already covers the sparse-matrix hole (Mini × Aetheric on Beleth).
  // We deliberately do NOT re-cascade just because the resolved variant is
  // sold out: sold-out combos stay configurable and the ATC button carries
  // buyability. (A fully sold-out product marks every variant-picker option
  // data-option-available="false", so the old sold-out guard here wiped the
  // customer's picks on every single color change — see Salos.)
  // The reverse (Custom → Signatures when signatures become available) is
  // intentionally not handled — yanking the customer off Custom is jarring.
  #syncAfterVariantUpdate(event) {
    const newId = String(event?.detail?.resource?.id || '');
    if (!newId) return;
    const gating = this.#variantGating.get(newId);
    if (!gating) return;
    this.dataset.variantId = newId;
    this.#applyingDefaults = true;
    try {
      this.#applyVariantGating(gating);
      this.#fallbackToCustomIfSignaturesEmpty();
      // Mid-build guard: with the Custom category active and nothing picked
      // yet, the picker reads as invalid — but a morph landing while the
      // builder dialog is open (e.g. from a mode switch's own Pour sync
      // seconds earlier) must NOT re-cascade to the first Signature behind
      // the customer's back. Apply/cancel re-validates on close.
      if (!this.#isCurrentSelectionValid() && !this.#isCustomDialogOpen()) {
        this.#applyDefaultSelection();
      }
    } finally {
      this.#applyingDefaults = false;
    }
    this.#updateValidity();
    this.#syncPourStyle();
  }

  // The picker's `dataset.sectionId` is actually the picker_scope — on PDP
  // that's section.id; on the configurator it's `section.id-block.id`. The
  // sibling variant-picker receives the same scope via `section_id_override`
  // and stores it on its own `data-section-id`, so the equality lookup is
  // load-bearing despite the naming.
  #pickerScope() {
    return this.dataset.sectionId || '';
  }

  // Per-variant gating: gray out chips outside gating.surfaces[region];
  // hide Type pills for the same types; hide signature pills
  // whose handle isn't in gating.signatures or whose cost isn't in
  // gating.pour_style_costs. Uncheck pills that become hidden.
  #applyVariantGating(gating) {
    this.#currentGating = gating;

    for (const chip of this.#refList('chips')) {
      const allowed = gating.surfaces?.[chip.dataset.position];
      const enabled = Array.isArray(allowed) && allowed.includes(chip.dataset.swatchType);
      chip.dataset.disabled = enabled ? 'false' : 'true';
    }

    // Type rows render union-of-variants. Without per-variant filtering,
    // the customer could pick a Type whose chips are all gated-out (empty grid).
    const positionsTouched = new Set();
    for (const group of this.#refList('typeGroups')) {
      const position = group.dataset.position;
      const allowedRaw = gating.surfaces?.[position];
      const allowedSet = new Set(Array.isArray(allowedRaw) ? allowedRaw : []);
      let clearedChecked = false;
      for (const input of this.#optionsIn(group)) {
        this.#setOptionAvailable(input, allowedSet.has(input.value));
        if (input.disabled && input.checked) {
          input.checked = false;
          clearedChecked = true;
        }
      }
      if (clearedChecked) this.#typeSelections[position] = null;
      positionsTouched.add(position);
    }

    // Category options depend on `(position, type)`; if a type just got
    // hidden, the matching category options need a re-pass too — otherwise
    // the user could see categories whose chips are all gated for this variant
    // until they touch the type row.
    for (const position of positionsTouched) {
      this.#filterCategories(position);
    }

    const sigSet = new Set(gating.signatures || []);
    const costSet = new Set((gating.pour_style_costs || []).map(Number));
    for (const pill of this.#refList('signaturePills')) {
      const total = Number(pill.dataset.totalCost) || 0;
      const allowed = sigSet.has(pill.dataset.handle) && costSet.has(total);
      const label = pill.closest('label');
      if (label) label.dataset.disabled = allowed ? 'false' : 'true';
      if (!allowed && pill.checked) pill.checked = false;
      pill.disabled = !allowed;
    }
  }

  // Build per-instance surface map from the rendered region tabs.
  // Trust the data-* attrs as source of truth — JS never needs to know
  // the surface_name → handle mapping rules itself.
  #initSurfaces() {
    const list = this.#refList('regionTabs');
    const positions = {};
    const selections = {};
    const typeSelections = {};
    const categorySelections = {};
    const sparkleSelections = {};
    const splitKeys = [];
    let modelKey = null;
    for (const tab of list) {
      const key = tab.dataset.position;
      if (!key) continue;
      const isModel = tab.dataset.isModel === 'true';
      positions[key] = {
        propertyKey: tab.dataset.propertyKey || `${tab.dataset.surfaceName} Color`,
        handleKey: tab.dataset.handleKey || `_${tab.dataset.surfaceName} Color Handle`,
        sparkleKey: tab.dataset.sparkleKey || `${tab.dataset.surfaceName} Sparkles`,
        surfaceName: tab.dataset.surfaceName || key,
        isModel,
      };
      // Preserve state across re-runs.
      selections[key] = this.#selections[key] ?? null;
      typeSelections[key] = this.#typeSelections[key] ?? null;
      categorySelections[key] = this.#categorySelections[key] ?? null;
      sparkleSelections[key] = this.#sparkleSelections[key] ?? false;
      if (isModel && !modelKey) modelKey = key;
      else if (!isModel) splitKeys.push(key);
    }
    this.#positions = positions;
    this.#selections = selections;
    this.#typeSelections = typeSelections;
    this.#categorySelections = categorySelections;
    this.#sparkleSelections = sparkleSelections;
    this.#modelKey = modelKey;
    this.#splitKeys = splitKeys;
    this.#modeRegions = {
      entire: modelKey ? [modelKey] : [],
      split: splitKeys,
    };
  }

  // Bootstrap. #applyingDefaults suppresses the Shaft→Base auto-advance
  // during init so there's no Base flicker on first paint. Signature path
  // routes through the same chip-click flow, so the cascade no-ops if one matches.
  #initialize() {
    this.#mode = this.dataset.initialMode === 'entire' ? 'entire' : 'split';
    this.#activeRegion = this.#modeRegions[this.#mode]?.[0] ?? null;
    this.#activeTab = this.#detectActiveTab();
    this.#fallbackToCustomIfSignaturesEmpty();
    this.#applyActiveRegion();
    this.#sortChips();
    this.#buildCategoryIndex();
    this.#buildChipIndex();
    // Snapshot before restore so the post-cascade catch-up below can tell
    // cold load (apply sparkles_by_default) from re-init (preserve cache).
    const hadCachedSparkle = SwatchPicker.#sparkleCache.has(this.#sparkleCacheKey());
    this.#restoreSparkleState();

    this.#applyDefaultSelection();

    // selectSwatch gates the auto-sparkle behind !#applyingDefaults (to
    // protect cached picks). Cold load has no cache to protect — apply
    // each surface's flag now so the 3D viewer paints with sparkle on
    // first paint.
    if (!hadCachedSparkle) {
      for (const position of this.#modeRegions[this.#mode] || []) {
        this.#applyColdLoadSparkleDefault(position);
      }
    }

    this.#updateValidity();
    this.#syncPourStyle();
  }

  // Cold load + post-variant fallback when picks aren't buyable. Save/restore
  // #applyingDefaults (don't blanket-clear) — #autoSelectFirstSignature →
  // selectSignature → #applyMode nests its own =true block and a finally
  // blanket-clear would strand the inner scope mid-cascade.
  #applyDefaultSelection() {
    for (const position of this.#modeRegions[this.#mode] || []) {
      if (this.#selections[position]) this.#clearSelection(position);
      this.#typeSelections[position] = null;
      this.#categorySelections[position] = null;
      this.#sparkleSelections[position] = false;
    }
    this.#clearSignaturePills();

    const prevApplyingDefaults = this.#applyingDefaults;
    this.#applyingDefaults = true;
    try {
      this.#autoSelectFirstSignature();
      for (const position of this.#modeRegions[this.#mode] || []) {
        this.#autoSelectDefaults(position);
      }
      // Safety net: if #autoSelectDefaults's filter cascade dead-ended, pick
      // the first enabled chip regardless of Type/Category; #applySwatchById
      // drives the Type/Category rows to match.
      for (const position of this.#modeRegions[this.#mode] || []) {
        if (this.#selections[position]) continue;
        const area = this.#chipAreaFor(position);
        // Same unconfigured-Custom skip as #autoSelectDefaults.
        const anyChip = area
          ? Array.from(area.querySelectorAll('.swatch-chip:not([data-disabled="true"])')).find(
              (c) => c.dataset.custom !== 'true' || c.dataset.color
            )
          : null;
        const swatchId = anyChip?.dataset.swatchId;
        if (swatchId) this.#applySwatchById(position, swatchId);
      }

      // The structural cascade isn't cost-aware. If signatures are unauthored
      // AND the chip-first picks sum outside pour_style_costs, force a valid
      // combo via entire-mode + lowest-allowed Model chip.
      if (!this.#isCurrentSelectionValid()) {
        this.#applyValidCostFallback();
      }
    } finally {
      this.#applyingDefaults = prevApplyingDefaults;
    }
  }

  // Switch to entire mode (single Model chip → no summing surprises) and
  // pick the lowest-cost chip whose cost is in pour_style_costs. Bails if
  // the variant has no Model surface — split-mode multi-region cost search
  // is out of scope.
  #applyValidCostFallback() {
    const allowedCosts = new Set(
      ((this.#currentGating?.pour_style_costs) || []).map(Number)
    );
    if (allowedCosts.size === 0) return;
    if (!this.#modelKey || this.#modeRegions.entire.length === 0) return;

    if (this.#mode !== 'entire') this.#applyMode('entire');

    // #applyMode runs its own structural cascade on Model — if that already
    // landed on an allowed cost, we're done.
    if (this.#isCurrentSelectionValid()) return;

    const minCost = Math.min(...allowedCosts);
    const area = this.#chipAreaFor(this.#modelKey);
    if (!area) return;
    const chips = Array.from(
      area.querySelectorAll('.swatch-chip:not([data-disabled="true"])')
    );
    const target = chips.find((c) => Number(c.dataset.colorCost) === minCost);
    if (target) this.#applySwatchById(this.#modelKey, target.dataset.swatchId);
  }

  // First Signature whose label isn't disabled — gating in #applyVariantGating
  // already filtered by signatures + pour_style_costs.
  #autoSelectFirstSignature() {
    const target = this.#refList('signaturePills').find((pill) => {
      if (!(pill instanceof HTMLInputElement)) return false;
      if (pill.disabled) return false;
      const label = pill.closest('label');
      return label ? label.dataset.disabled !== 'true' : true;
    });

    if (!target) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  selectSwatch(event) {
    const button = event.target.closest('.swatch-chip');
    if (!button) return;
    // CSS pointer-events: none blocks real clicks, but #applyDefaultSelection
    // routes through chip.click() — short-circuit gated chips here.
    if (button.dataset.disabled === 'true') return;

    // Custom "pick your own" chips: a direct click opens the builder
    // (prefilled when already configured); internal clicks — Apply's own
    // re-entry, mode-snapshot restore, cascades — select the configured
    // combo through the normal flow below. Gate on the internal flags, NOT
    // event.isTrusted: every internal path sets a flag, and flag-gating
    // keeps the builder reachable from console/tests too. Unconfigured
    // chips have no colours — hard no-op outside the builder.
    if (button.dataset.custom === 'true') {
      if (!this.#applyingCustom && !this.#applyingDefaults && !this.#applyingSignature) {
        this.#openCustomDialog(button.dataset.position, button.dataset.swatchType);
        return;
      }
      if (!button.dataset.color) return;
    }

    const position = button.dataset.position;
    if (!this.#positions[position]) return;

    const cost = Number(button.dataset.colorCost) || 0;
    const label = button.dataset.label || '';
    const swatchId = button.dataset.swatchId || '';

    this.#selections[position] = { button, cost, label, swatchId };

    for (const chip of this.#chipsFor(position)) {
      chip.setAttribute('aria-pressed', chip === button ? 'true' : 'false');
    }

    // Auto-arm Sparkles to the picked swatch's `sparkles_by_default` flag
    // on user-driven picks (direct click, Signature, Type/Category cascade
    // landing on a chip). #applyingDefaults gates out cold-load init and
    // Mode-toggle snapshot restore (both wrap their work in =true).
    if (!this.#applyingDefaults) {
      const sparklesDefault = button.dataset.sparklesDefault === 'true';
      if (this.#sparkleSelections[position] !== sparklesDefault) {
        this.#sparkleSelections[position] = sparklesDefault;
        const cb = this.querySelector(
          `.swatch-picker__sparkle-input[data-position="${position}"]`
        );
        if (cb) cb.checked = sparklesDefault;
        this.#persistSparkleState();
      }
    }

    this.#writeProperty(position, label);
    this.#writeHandleProperty(position, button.dataset.swatchHandle || '');
    this.#writeSparkleProperty(position, this.#sparkleSelections[position]);
    // Custom combos only — catalog chips have no pour breakdown (their
    // label IS the recipe name), so the input stays disabled for them.
    this.#writePourProperty(position, button.dataset.customPour || '');
    this.#paintRegionPreview(position, button);

    // Reconcile signature radio against current picks. Skipped during an
    // active signature application — that flow manages pill state itself.
    if (!this.#applyingSignature) this.#syncSignaturePillForSelection();

    // Split mode: advance the active tab to the next un-picked surface so
    // the customer notices remaining picks. Skipped during cascade to avoid
    // flicker through partner tabs.
    if (
      this.#mode === 'split' &&
      !this.#applyingDefaults &&
      this.#splitKeys.includes(position)
    ) {
      const nextEmpty = this.#splitKeys.find(
        (k) => k !== position && !this.#selections[k]
      );
      if (nextEmpty) {
        this.#activeRegion = nextEmpty;
        this.#applyActiveRegion();
      }
    }

    this.#updateValidity();
    this.#syncPourStyle();
    this.#emitSwatchChange(position, button);
  }

  // Tick the signature whose data-applies-to pairs match current picks;
  // untick all otherwise. Entire-mode: model pick must equal every pair id.
  // Split-mode: every split surface must be filled and match the pair.
  #syncSignaturePillForSelection() {
    const list = this.#refList('signaturePills');
    // Skip disabled pills so picks that happen to sum to a disabled
    // signature don't tick a grayed-out pill.
    const enabled = list.filter((p) => !p.disabled);

    let matched = null;

    if (this.#mode === 'entire' && this.#modelKey) {
      const modelId = this.#selections[this.#modelKey]?.swatchId;
      if (modelId) {
        matched = enabled.find((p) => {
          const pairs = parseAppliesTo(p.dataset.appliesTo);
          if (pairs.length === 0) return false;
          return pairs.every(([_, id]) => id === modelId);
        }) || null;
      }
    } else if (this.#mode === 'split' && this.#splitKeys.length > 0) {
      // Every split surface must have a pick.
      const filled = this.#splitKeys.every((k) => Boolean(this.#selections[k]));
      if (filled) {
        matched = enabled.find((p) => {
          const pairs = parseAppliesTo(p.dataset.appliesTo);
          if (pairs.length !== this.#splitKeys.length) return false;
          const pillMap = Object.fromEntries(pairs);
          return this.#splitKeys.every(
            (k) => pillMap[k] && pillMap[k] === this.#selections[k]?.swatchId
          );
        }) || null;
      }
    }

    for (const input of list) {
      input.checked = input === matched;
    }
  }

  // Surface selection to outside listeners (3D viewer is the first consumer).
  #emitSwatchChange(position, button) {
    this.dispatchEvent(new CustomEvent('bunny:swatch-change', {
      bubbles: true,
      composed: true,
      detail: {
        region: position,
        mode: this.#mode,
        swatch: {
          id: button.dataset.swatchId,
          type: button.dataset.swatchType,
          color: button.dataset.color,
          color2: button.dataset.color2,
          // Custom 3-colour fades only; undefined otherwise.
          colorMid: button.dataset.colorMid,
          label: button.dataset.label,
          sparkle: this.#sparkleSelections[position],
        },
      },
    }));
  }

  // Update per-region flag, mirror to cart input, re-emit swatch-change so
  // the 3D viewer flips sparkle on/off.
  selectSparkle(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;
    const position = checkbox.dataset.position;
    if (!position || !(position in this.#sparkleSelections)) return;
    this.#sparkleSelections[position] = checkbox.checked;
    this.#persistSparkleState();
    this.#writeSparkleProperty(position, checkbox.checked);
    const sel = this.#selections[position];
    if (sel?.button) this.#emitSwatchChange(position, sel.button);
  }

  #sparkleCacheKey() {
    return this.#pickerScope();
  }

  #persistSparkleState() {
    SwatchPicker.#sparkleCache.set(this.#sparkleCacheKey(), { ...this.#sparkleSelections });
  }

  // Cold-load only (see #initialize). Aligns a region's sparkle to its
  // chip's `sparkles_by_default`, mirrors to checkbox + cart, re-emits.
  #applyColdLoadSparkleDefault(position) {
    const sel = this.#selections[position];
    if (!sel?.button) return;
    const want = sel.button.dataset.sparklesDefault === 'true';
    if (this.#sparkleSelections[position] === want) return;
    this.#sparkleSelections[position] = want;
    const cb = this.querySelector(
      `.swatch-picker__sparkle-input[data-position="${position}"]`
    );
    if (cb) cb.checked = want;
    this.#writeSparkleProperty(position, want);
    this.#persistSparkleState();
    this.#emitSwatchChange(position, sel.button);
  }

  // Pull cached sparkle picks into #sparkleSelections + the DOM checkboxes.
  // Runs before the default cascade so the cascade's emits carry the right flag.
  #restoreSparkleState() {
    const cached = SwatchPicker.#sparkleCache.get(this.#sparkleCacheKey());
    if (cached) {
      for (const position of Object.keys(this.#sparkleSelections)) {
        this.#sparkleSelections[position] = !!cached[position];
      }
    }
    for (const cb of this.querySelectorAll('.swatch-picker__sparkle-input')) {
      const pos = cb.dataset.position;
      if (pos && pos in this.#sparkleSelections) {
        cb.checked = !!this.#sparkleSelections[pos];
      }
    }
  }

  selectTab(event) {
    const button = event.target.closest('.swatch-picker__tab');
    if (!button) return;
    this.#activeTab = button.dataset.tab;
    this.#applyActiveTab();
  }

  // Mirror Liquid's aria-selected="true". Falls back to 'custom' for
  // single-tab pickers.
  #detectActiveTab() {
    const active = this.#refList('tabs').find((t) => t.getAttribute('aria-selected') === 'true');
    return active?.dataset.tab || 'custom';
  }

  #hasEnabledSignature() {
    return this.#refList('signaturePills').some((p) => p instanceof HTMLInputElement && !p.disabled);
  }

  // Fall back to Custom only when on Signatures AND every pill is gated out.
  // Don't yank a customer who manually picked Custom back to Signatures on
  // a later variant change.
  #fallbackToCustomIfSignaturesEmpty() {
    if (this.#activeTab !== 'signatures') return;
    if (this.#hasEnabledSignature()) return;
    const list = this.#refList('tabs');
    if (!list.some((t) => t.dataset.tab === 'custom')) return;
    this.#activeTab = 'custom';
    this.#applyActiveTab();
  }

  #applyActiveTab() {
    const tabList = this.#refList('tabs');
    if (tabList.length === 0) return;
    if (!tabList.some((t) => t.dataset.tab === this.#activeTab)) {
      this.#activeTab = 'custom';
    }
    for (const t of tabList) {
      t.setAttribute('aria-selected', t.dataset.tab === this.#activeTab ? 'true' : 'false');
    }
    for (const panel of this.#refList('tabPanels')) {
      panel.hidden = panel.dataset.tab !== this.#activeTab;
    }
  }

  selectRegion(event) {
    const button = event.target.closest('.swatch-picker__region-tab');
    if (!button) return;
    const position = button.dataset.position;
    if (!this.#positions[position]) return;
    this.#activeRegion = position;
    this.#applyActiveRegion();
  }

  #applyActiveRegion() {
    for (const region of Object.keys(this.#positions)) {
      const group = this.#groupFor(region);
      if (group) group.hidden = region !== this.#activeRegion;
      const tab = this.#regionTabFor(region);
      if (tab) tab.setAttribute('aria-selected', region === this.#activeRegion ? 'true' : 'false');
    }
  }

  // Re-assert mode-driven DOM state (tab/group visibility + radio).
  // GOTCHA on the `input.checked = …` write — see #applyMode header.
  #applyModeVisibility() {
    for (const input of this.#refList('modePills')) {
      input.checked = input.value === this.#mode;
    }
    const activeSet = new Set(this.#modeRegions[this.#mode] || []);
    for (const region of Object.keys(this.#positions)) {
      const tab = this.#regionTabFor(region);
      if (tab) tab.hidden = !activeSet.has(region);
    }
    if (!activeSet.has(this.#activeRegion)) {
      this.#activeRegion = (this.#modeRegions[this.#mode] || [])[0] ?? null;
    }
    this.#applyActiveRegion();
  }

  // Apply a Signature Classic. Mode resolution: authored `data-selection-mode`
  // wins; if 'entire' and the variant lacks a Model chip, fall back to split.
  // Legacy fallback (unset): infer Entire when every swatchId is identical
  // AND a Model chip exists. All 15 combos were seeded 2026-05-13 — fallback
  // only triggers on author error.
  selectSignature(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    const pairs = parseAppliesTo(target.dataset.appliesTo);
    if (pairs.length === 0) return;

    const swatchIds = pairs.map(([, id]) => id);
    const sharedId = swatchIds.every((id) => id === swatchIds[0]) ? swatchIds[0] : null;
    const modelGroupExists = this.#modelKey
      ? Boolean(this.refs[`${this.#modelKey}Group`])
      : false;
    // INVARIANT: modelChipExists ⇒ sharedId truthy (the AND short-circuits).
    // So every branch below that picks `entire` only fires with a real sharedId
    // to feed into #applySwatchById(modelKey, sharedId).
    const modelChipExists = sharedId && modelGroupExists &&
      this.#chipsFor(this.#modelKey).some((c) => c.dataset.swatchId === sharedId);
    const authoredMode = target.dataset.selectionMode;
    let targetMode;
    if (authoredMode === 'entire') {
      targetMode = modelChipExists ? 'entire' : 'split';
    } else if (authoredMode === 'split') {
      targetMode = 'split';
    } else {
      // Legacy fallback: infer Entire only when picks are identical AND a
      // Model chip exists. Per the invariant above this is equivalent to
      // `sharedId && modelChipExists`.
      targetMode = modelChipExists ? 'entire' : 'split';
    }

    this.#applyingSignature = true;
    try {
      if (this.#mode !== targetMode) {
        this.#applyMode(targetMode);
      }

      if (targetMode === 'entire') {
        this.#applySwatchById(this.#modelKey, sharedId);
      } else {
        for (const [pos, id] of pairs) {
          if (!this.#positions[pos]) continue;
          this.#applySwatchById(pos, id);
        }
      }
    } finally {
      this.#applyingSignature = false;
    }
    this.#syncPourStyle();
  }

  // Drive a region to a specific swatch ID. Routes through chip.click() so
  // the full selectSwatch flow runs (cart prop, validity, Pour Style).
  // Silent no-op if the swatch isn't in the chip set — callers in #applyMode
  // rely on this so a stale snapshot falls through to #autoSelectDefaults.
  #applySwatchById(position, swatchId) {
    const chip = this.#chipsFor(position).find((c) => c.dataset.swatchId === swatchId);
    if (!chip) return;
    const type = chip.dataset.swatchType;
    const category = chip.dataset.category;
    if (type && this.#typeSelections[position] !== type) {
      this.#applyTypeSelection(position, type);
    }
    if (category && this.#categorySelections[position] !== category) {
      this.#applyCategorySelection(position, category);
    }
    if (this.#selections[position]?.button !== chip) {
      chip.click();
    }
  }

  #clearSignaturePills() {
    for (const input of this.#refList('signaturePills')) input.checked = false;
  }

  selectMode(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.checked) return;

    const mode = target.value;
    if (!mode || !this.#modeRegions[mode] || mode === this.#mode) return;

    this.#applyMode(mode);
  }

  // GOTCHA: programmatic `input.checked = …` writes from #applyModeVisibility
  // do NOT fire `change`. External consumers (mobile bar, viewer, analytics)
  // must key off `bunny:swatch-change`'s detail.mode, not the mode radio.
  // See memory `swatch_picker_apply_mode_no_change_event.md`.
  #applyMode(mode) {
    // Snapshot outgoing mode so a future toggle back restores it.
    const prevMode = this.#mode;
    if (prevMode && this.#modeRegions[prevMode]) {
      this.#modeState[prevMode] = {
        activeRegion: this.#activeRegion,
        selections: Object.fromEntries(
          this.#modeRegions[prevMode].map((r) => [r, this.#selections[r]?.swatchId || null])
        ),
      };
    }

    this.#mode = mode;

    // Clear selections outside the new mode's region set so they don't ride to cart.
    const activeSet = new Set(this.#modeRegions[mode] || []);
    for (const region of Object.keys(this.#positions)) {
      if (!activeSet.has(region) && this.#selections[region]) {
        this.#clearSelection(region);
      }
    }

    // Prefer the snapshot's active region; #applyModeVisibility falls back.
    const snapshotRegion = this.#modeState[mode]?.activeRegion;
    if (snapshotRegion && activeSet.has(snapshotRegion)) {
      this.#activeRegion = snapshotRegion;
    }
    this.#applyModeVisibility();

    // Restore snapshot picks; fall through to defaults for uncovered regions.
    // Save+restore #applyingDefaults rather than blanket-clear at finally —
    // nested selectSignature → #applyMode during #initialize would otherwise
    // get stranded mid-cascade.
    const prevApplyingDefaults = this.#applyingDefaults;
    this.#applyingDefaults = true;
    try {
      const snapshot = this.#modeState[mode];
      const regionsForMode = this.#modeRegions[mode] || [];
      if (snapshot) {
        for (const position of regionsForMode) {
          const swatchId = snapshot.selections[position];
          if (swatchId) this.#applySwatchById(position, swatchId);
        }
      }
      for (const position of regionsForMode) {
        this.#autoSelectDefaults(position);
      }
    } finally {
      this.#applyingDefaults = prevApplyingDefaults;
    }

    this.#updateValidity();
    this.#syncPourStyle();
  }

  #groupFor(position) {
    return this.refs[`${position}Group`] || null;
  }

  selectType(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.disabled || !input.checked) return;

    const position = input.dataset.position;
    const type = input.value;
    if (!this.#positions[position] || !type) return;

    this.#applyTypeSelection(position, type);
  }

  selectCategory(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.disabled || !input.checked) return;

    const position = input.dataset.position;
    const category = input.value;
    if (!this.#positions[position] || !category) return;

    this.#applyCategorySelection(position, category);
  }

  #applyTypeSelection(position, type) {
    // Remember the prior category to carry across the type switch when possible.
    const priorCategory = this.#categorySelections[position];

    this.#typeSelections[position] = type;

    this.#setGroupValue(this.#typeGroupFor(position), type);

    // New Type invalidates the prior category — available cats depend on type.
    this.#clearCategorySelection(position);
    const categoryGroup = this.#categoryGroupFor(position);

    const prompt = this.#promptFor(position);
    if (prompt) {
      prompt.hidden = false;
      prompt.textContent = prompt.dataset.promptCategory;
    }

    // Prior chip is almost certainly a different type — clear it.
    if (this.#selections[position]) this.#clearSelection(position);

    this.#filterCategories(position);

    // Preserve category across type switch (Solid → Faded in same category).
    if (priorCategory && categoryGroup) {
      const priorInput = this.#optionsIn(categoryGroup).find((i) => i.value === priorCategory);
      if (priorInput && !priorInput.disabled) {
        this.#applyCategorySelection(position, priorCategory);
      }
    }

    this.#autoSelectDefaults(position);
    this.#updateValidity();
    this.#syncPourStyle();
  }

  #applyCategorySelection(position, category) {
    this.#categorySelections[position] = category;

    this.#setGroupValue(this.#categoryGroupFor(position), category);

    const area = this.#chipAreaFor(position);
    const activeType = this.#typeSelections[position];
    if (area) {
      let anyMatch = false;
      for (const grid of area.querySelectorAll('.swatch-picker__chips')) {
        const matches = grid.dataset.swatchType === activeType && grid.dataset.category === category;
        grid.dataset.visible = matches ? 'true' : 'false';
        if (matches) anyMatch = true;
      }
      // No group for this Type + Category combo → show the empty-state message
      // instead of a blank area. #filterCategories now pulls empty combos out of
      // the Category row entirely, so this is a safety net rather than the iOS
      // Safari <option hidden> workaround it started as.
      const empty = area.querySelector('.swatch-picker__empty');
      if (empty) empty.hidden = anyMatch;
      // Reset scroll so the customer lands on the first chip of the new category.
      area.scrollTop = 0;
      // Swapping grids changes scrollHeight inside an unchanged box, which the
      // rail's ResizeObserver can't see — and scrollTop was already 0 on most
      // swaps, so the scroll event won't fire either. On mobile the same swap
      // resizes the drawer's scrollable content instead.
      this.#chipScrollbars.get(position)?.update();
      this.#drawerScrollbar?.update();
    }

    const prompt = this.#promptFor(position);
    if (prompt) prompt.hidden = true;

    if (this.#selections[position]) this.#clearSelection(position);

    // Continue the cascade. selectSwatch handles validity + Pour Style sync;
    // updateValidity below covers the edge where no chip is available.
    this.#autoSelectDefaults(position);
    this.#updateValidity();
    this.#syncPourStyle();
  }

  #clearCategorySelection(position) {
    this.#categorySelections[position] = null;
    this.#clearGroupValue(this.#categoryGroupFor(position));
    const area = this.#chipAreaFor(position);
    if (area) {
      for (const grid of area.querySelectorAll('.swatch-picker__chips')) {
        grid.dataset.visible = 'false';
      }
      // Prompt takes over ("Pick a Category…") — clear any empty-state message.
      const empty = area.querySelector('.swatch-picker__empty');
      if (empty) empty.hidden = true;
      // Every grid just went display:none — retire the rail (see the note in
      // #applyCategorySelection for why the observers don't cover this).
      this.#chipScrollbars.get(position)?.update();
      this.#drawerScrollbar?.update();
    }
  }

  // Find the entry in a per-region ref array (`ref="...[]"`) by data-position.
  #refByPosition(refName, position) {
    return this.#refList(refName).find((el) => el.dataset.position === position) || null;
  }

  #typeGroupFor(position) { return this.#refByPosition('typeGroups', position); }
  #categoryGroupFor(position) { return this.#refByPosition('categoryGroups', position); }
  #promptFor(position) { return this.#refByPosition('prompts', position); }
  #chipAreaFor(position) { return this.#refByPosition('chipAreas', position); }
  #regionTabFor(position) { return this.#refByPosition('regionTabs', position); }
  #regionTabPreviewFor(position) { return this.#refByPosition('regionTabPreviews', position); }

  // ── Type / Category radio groups ──────────────────────────────────
  // These were <select>s until the radio-pill rework. The group is the
  // <fieldset> (carries the ref + data-position); its radios are queried by
  // class. `input.disabled` is the availability flag — the role that
  // `<option hidden>` used to play — and every read below keys off it, so an
  // unavailable option is unreachable by click, keyboard, and the
  // first-available cascade alike.

  #optionsIn(group) {
    return group ? Array.from(group.querySelectorAll('.swatch-picker__option-input')) : [];
  }

  // Hidden on the label (pill leaves the row) + disabled on the input (JS
  // flag, and keeps it out of the radio group's keyboard order).
  #setOptionAvailable(input, available) {
    input.disabled = !available;
    const label = input.closest('.swatch-picker__option');
    if (label) label.hidden = !available;
  }

  #setGroupValue(group, value) {
    for (const input of this.#optionsIn(group)) input.checked = input.value === value;
  }

  #clearGroupValue(group) {
    for (const input of this.#optionsIn(group)) input.checked = false;
  }

  // Clone the chip's .swatch-chip__visual into the region tab preview slot.
  // The clone carries its own --swatch-color inline style and inner markup,
  // so it renders identically.
  #paintRegionPreview(position, button) {
    const preview = this.#regionTabPreviewFor(position);
    if (!preview) return;
    preview.replaceChildren();
    if (!button) return;
    const visual = button.querySelector('.swatch-chip__visual');
    if (visual) preview.appendChild(visual.cloneNode(true));
  }

  // Cascade: first Type → first Category → first chip. Each step short-
  // circuits if already set; Type/Category selections re-enter via the
  // cascade so chip-pick always runs against fresh filter state.
  #autoSelectDefaults(position) {
    if (!this.#typeSelections[position]) {
      const firstType = this.#optionsIn(this.#typeGroupFor(position)).find((i) => !i.disabled);
      if (firstType) {
        this.#applyTypeSelection(position, firstType.value);
        return;
      }
    }

    if (!this.#categorySelections[position]) {
      const firstCat = this.#optionsIn(this.#categoryGroupFor(position)).find((i) => !i.disabled);
      if (firstCat) {
        this.#applyCategorySelection(position, firstCat.value);
        return;
      }
    }

    if (!this.#selections[position]) {
      const area = this.#chipAreaFor(position);
      const chips = area
        ? Array.from(
            area.querySelectorAll(
              '.swatch-picker__chips[data-visible="true"] .swatch-chip:not([data-disabled="true"])'
            )
          )
        : [];
      // Skip UNCONFIGURED Custom chips — clicking one is a builder-only
      // no-op and would dead-end the cascade. A configured one (has
      // data-color) reselects, e.g. returning to the Custom category — but
      // its click must be flagged internal, or the builder would pop open
      // on a category pill click (this cascade runs unwrapped when the user
      // drives the Type/Category rows directly).
      const firstChip = chips.find((c) => c.dataset.custom !== 'true' || c.dataset.color);
      if (firstChip && firstChip.dataset.custom === 'true') {
        this.#applyingCustom = true;
        try {
          firstChip.click();
        } finally {
          this.#applyingCustom = false;
        }
      } else if (firstChip) {
        firstChip.click();
      }
    }
  }

  // Hide category pills whose category has no chips of the active type
  // (structural mismatch — budget-based disabling is gone).
  #filterCategories(position) {
    const activeType = this.#typeSelections[position];
    const categoryGroup = this.#categoryGroupFor(position);
    if (!categoryGroup) return;
    const categoriesForType = activeType
      ? this.#categoryIndex.get(position)?.get(activeType)
      : null;
    const options = this.#optionsIn(categoryGroup);
    for (const input of options) {
      this.#setOptionAvailable(input, Boolean(categoriesForType?.has(input.value)));
    }

    const active = this.#categorySelections[position];
    if (!active) return;
    const activeInput = options.find((i) => i.value === active);
    if (activeInput && activeInput.disabled) this.#clearCategorySelection(position);
  }

  // Build #categoryIndex once so #filterCategories answers
  // "does this (position, type) have category X?" in O(1).
  #buildCategoryIndex() {
    for (const chip of this.#refList('chips')) {
      const position = chip.dataset.position;
      const type = chip.dataset.swatchType;
      const category = chip.dataset.category;
      if (!position || !type || !category) continue;
      let perPosition = this.#categoryIndex.get(position);
      if (!perPosition) {
        perPosition = new Map();
        this.#categoryIndex.set(position, perPosition);
      }
      let perType = perPosition.get(type);
      if (!perType) {
        perType = new Set();
        perPosition.set(type, perType);
      }
      perType.add(category);
    }
  }

  // Built once after #sortChips so #chipsFor is O(1) instead of
  // O(total-chips). Refs.chips is stable across the picker's lifetime
  // (render-once + gating-only-on-variant-update), so a one-shot index is safe.
  #buildChipIndex() {
    this.#chipsByPosition.clear();
    for (const chip of this.#refList('chips')) {
      const pos = chip.dataset.position;
      if (!pos) continue;
      let arr = this.#chipsByPosition.get(pos);
      if (!arr) { arr = []; this.#chipsByPosition.set(pos, arr); }
      arr.push(chip);
    }
  }

  #clearSelection(position) {
    this.#selections[position] = null;
    for (const chip of this.#chipsFor(position)) {
      chip.setAttribute('aria-pressed', 'false');
    }
    this.#writeProperty(position, '');
    this.#writeHandleProperty(position, '');
    this.#writeSparkleProperty(position, false);
    this.#writePourProperty(position, '');
    this.#paintRegionPreview(position, null);
  }

  #chipsFor(position) {
    return this.#chipsByPosition.get(position) || [];
  }

  // GOTCHA: `section.id` is blank inside nested theme blocks (PDP renders
  // this picker inside `_product-details`), so the Liquid-computed form_id
  // is the bare prefix `BuyButtons-ProductForm-` and FormData drops the
  // properties on submit. Resolve the real form id at runtime.
  #bindHiddenInputsToForm() {
    const scope = this.#hostPanel || this.closest('.shopify-section');
    const form = scope?.querySelector('form[data-type="add-to-cart-form"]');
    const formId = form?.getAttribute('id');
    if (!formId) {
      // Spot in DevTools: querySelectorAll('[data-qa-form-orphan="true"]').
      console.warn(
        '[swatch-picker] could not resolve add-to-cart form id — line-item properties will not submit',
        { picker: this, scope }
      );
      this.dataset.qaFormOrphan = 'true';
      return;
    }
    this.dataset.qaFormOrphan = 'false';
    for (const region of Object.keys(this.#positions)) {
      for (const suffix of ['', 'Handle', 'Sparkle', 'Pour']) {
        this.refs[`${region}${suffix}Input`]?.setAttribute('form', formId);
      }
    }
  }

  // Hidden inputs follow `{position}{Suffix}Input` where Suffix is ''
  // (label), 'Handle', 'Sparkle', or 'Pour'.
  //
  // GOTCHA: blank means "surface not in play" (inactive region, cleared pick,
  // Sparkles off) — and Shopify does NOT strip empty line-item properties on
  // an Ajax /cart/add, so an emptied input still lands in the cart and the
  // admin order as `"Shaft Color": ""`. Disable it instead: a disabled control
  // is skipped by FormData and by a native form post, so the line carries only
  // the surfaces the customer actually picked. Re-enabled on the next write
  // with a value.
  #writeRegionInput(position, suffix, value) {
    const ref = this.refs[`${position}${suffix}Input`];
    if (!ref) return;
    ref.value = value;
    ref.disabled = value === '';
  }

  #writeProperty(position, value) {
    this.#writeRegionInput(position, '', value);
  }

  // Writes the canonical `{Surface} Sparkles` cart line-item property.
  #writeSparkleProperty(position, checked) {
    const value = checked ? 'Yes' : '';
    this.#writeRegionInput(position, 'Sparkle', value);
  }

  #writeHandleProperty(position, handle) {
    this.#writeRegionInput(position, 'Handle', handle || '');
  }

  // `_{Surface} Pour` — the admin-facing pour recipe for custom combos.
  #writePourProperty(position, value) {
    this.#writeRegionInput(position, 'Pour', value || '');
  }

  // Entire: Model cost stands alone. Split: sum every surface's cost,
  // with a Monolithic shortcut when all picks share one swatch id (cost
  // counts once). Shortcut applies even to an authored-Split combo whose
  // surfaces happen to share a swatch — "one color across every surface"
  // pays single cost regardless of path.
  #totalCost() {
    if (this.#mode === 'entire') {
      if (!this.#modelKey) return 0;
      return this.#selections[this.#modelKey]?.cost || 0;
    }
    const picks = this.#splitKeys.map((k) => this.#selections[k]);
    if (picks.length === 0 || picks.some((p) => !p)) return 0;
    const firstId = picks[0].swatchId;
    const allSame = firstId && picks.every((p) => p.swatchId === firstId);
    if (allSame) return picks[0].cost || 0;
    return picks.reduce((acc, p) => acc + (p.cost || 0), 0);
  }

  // True iff current picks resolve to a buyable variant. Fails on: missing
  // pick, disabled chip (gating's surface_N_swatch_types doesn't include
  // it), or total cost outside pour_style_costs. Opens up when
  // #currentGating isn't populated yet — cascade will fix it.
  #isCurrentSelectionValid() {
    const regions = this.#modeRegions[this.#mode] || [];
    if (regions.length === 0) return false;
    for (const position of regions) {
      const sel = this.#selections[position];
      if (!sel?.button) return false;
      if (sel.button.dataset.disabled === 'true') return false;
    }
    const total = this.#totalCost();
    if (!COST_TO_POUR_STYLE[total]) return false;
    const allowed = this.#currentGating?.pour_style_costs;
    if (!Array.isArray(allowed)) return true;
    return allowed.includes(total);
  }

  #updateValidity() {
    const total = this.#totalCost();
    const pourStyle = COST_TO_POUR_STYLE[total];
    const valid = total > 0 && Boolean(pourStyle);

    this.dataset.valid = valid ? 'true' : 'false';
    this.#toggleAddToCart(valid);
    this.#updateSelectedLabels();
    this.#updatePourStyleDisplay(pourStyle, total);
    const signatureName = this.#updateSignatureClassicName();

    // Suppress during cascade — intermediate ticks have only one region
    // picked, so total=0 would fade the halo out via the 400ms CSS
    // transition then back in. Trailing updateValidity() runs after
    // #applyingDefaults clears, so the real dispatch still fires once.
    if (!this.#applyingDefaults) {
      this.dispatchEvent(new CustomEvent('bunny:rarity-change', {
        bubbles: true,
        composed: true,
        detail: {
          cost: total,
          rarity: COST_TO_RARITY[total] || null,
          pourStyle: pourStyle || null,
          // Mobile has no inline pour-style readout (the whole picker is
          // hidden behind the bubble bar), so the bar's status row is the
          // only surface for the signature name — piggyback it on this event.
          signatureName: signatureName || null,
        },
      }));
    }
  }

  // The Signature Classic name shown above the rarity badge. Reads the
  // currently-checked signature pill — the same checked-state that
  // #syncSignaturePillForSelection sets when custom picks resolve to a known
  // combo (the auto-highlight), and that a direct pill click sets too. One
  // source of truth, so the name tracks the highlight through every path;
  // no matching pill → no name, matching the empty rarity badge.
  #updateSignatureClassicName() {
    const active = this.#refList('signaturePills').find((p) => p.checked);
    const name = active?.dataset.name || '';
    const el = this.refs.signatureClassicName;
    if (el) {
      el.textContent = name;
      el.hidden = !name;
    }
    // Returned so the caller can forward it on bunny:rarity-change (mobile bar).
    return name;
  }

  #updatePourStyleDisplay(pourStyle, cost) {
    const display = this.refs.pourStyleValue;
    // Rarity text goes in the dedicated text span so the sibling
    // signature-name span (first child of the badge) isn't wiped. data-rarity
    // (colour + flanking dots) stays on the badge element itself.
    const textEl = this.refs.pourStyleText || display;
    if (!display || !textEl) return;
    const fallback = display.parentElement?.dataset.emptyLabel || '';
    const rarity = COST_TO_RARITY[cost];
    if (pourStyle && rarity) {
      // Show only the Pour Style name ("Aetheric"); the rarity tier word
      // ("Legendary") is dropped — data-rarity still drives the badge colour.
      textEl.textContent = pourStyle;
      display.dataset.rarity = String(cost);
    } else {
      textEl.textContent = fallback;
      delete display.dataset.rarity;
    }
  }

  #updateSelectedLabels() {
    for (const position of Object.keys(this.#positions)) {
      const ref = this.refs[`${position}SelectedLabel`];
      if (ref) ref.textContent = this.#selections[position]?.label || '';
    }
  }

  // The configurator only ever *blocks* a buy (incomplete pick, or a combo
  // that maps to no real variant); it never *forces* one. Buyability of a
  // fully-resolved combo is Horizon's call via the variant's `available`
  // flag — so a valid-but-sold-out configuration stays fully explorable
  // (pick colors, switch modes, preview, rarity) while the buy button keeps
  // Horizon's "Sold out" disabled state instead of being force-enabled.
  #toggleAddToCart(valid) {
    const form = this.closest('form');
    if (!form) return;
    const submitButton = form.querySelector('button[type="submit"][name="add"]');
    if (!submitButton) return;

    if (!valid) {
      submitButton.setAttribute('data-swatch-blocked', 'true');
      submitButton.disabled = true;
      return;
    }

    // Valid configuration → not blocked by us. Enable only if the resolved
    // variant is in stock; otherwise leave it disabled (Horizon's morph
    // already rendered the sold-out label) without flagging it swatch-blocked.
    submitButton.removeAttribute('data-swatch-blocked');
    submitButton.disabled = this.#currentGating?.available === false;
  }

  // Drive the Pour Style variant axis from swatch picks. Clicks the radio
  // matching the target Pour Style; Horizon's variant-picker re-fetches +
  // morphs from there. Suppressed during cascade — intermediate chip clicks
  // would resolve partial totals and race the variant-picker into wrong
  // variants. Orchestrators call once after the cascade settles.
  #syncPourStyle() {
    if (this.#applyingSignature || this.#applyingDefaults) return;
    const total = this.#totalCost();
    const target = COST_TO_POUR_STYLE[total];
    if (!target) return;

    const scope = this.#pickerScope();
    const variantPicker = scope
      ? document.querySelector(`variant-picker[data-section-id="${scope}"]`)
      : document.querySelector('variant-picker');
    if (!variantPicker) return;

    const pourIndex = Number(this.dataset.pourStyleIndex);
    if (Number.isNaN(pourIndex)) return;

    const fieldset = variantPicker.querySelector(`fieldset[data-fieldset-index="${pourIndex}"]`);
    if (fieldset) {
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((r) => r.value === target);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // <select>-style fallback for non-radio option layouts.
    const select = Array.from(variantPicker.querySelectorAll('select')).find((s) =>
      Array.from(s.options).some((o) => Object.values(COST_TO_POUR_STYLE).includes(o.value))
    );
    if (select && select.value !== target) {
      select.value = target;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── "Pick your own" custom builder ─────────────────────────────────
  // Each qualifying type gets a single-chip grid under the synthetic
  // "Custom" category (its pill rides #filterCategories/#categoryIndex like
  // a real category). The Custom chip is a normal picker chip whose dataset
  // starts empty — a direct click opens the builder dialog, which fills it
  // (colours, cost, label, synthetic swatch id) and re-enters via
  // chip.click(), so cart properties, Pour Style math, gating, and the 3D
  // viewer all treat it exactly like a catalog chip. The handle property
  // carries a parseable `custom:{type}:{solids}` value (cart-products
  // renders the composite thumbnail from it) and `_{Surface} Pour` carries
  // the admin-facing recipe with hex codes.

  #initCustomDialog() {
    const dialog = this.querySelector('dialog-component.swatch-custom');
    if (!dialog) return;
    this.#customDialog = dialog;
    const blob = dialog.querySelector('script[type="application/json"][data-custom-labels]');
    if (blob) {
      try {
        Object.assign(this.#customLabels, JSON.parse(blob.textContent || '{}'));
      } catch (err) {
        console.warn('[swatch-picker] failed to parse custom labels JSON:', err);
      }
    }
    this.#onCustomDialogClickBound = (event) => this.#onCustomDialogClick(event);
    dialog.addEventListener('click', this.#onCustomDialogClickBound);

    // Same perceptual order as the region grids. All solids share cost 1,
    // so hue → saturation → lightness carries the sort. Static palette —
    // sorting once at init holds for the picker's lifetime.
    const optionsEl = dialog.querySelector('[data-custom-options]');
    if (optionsEl) {
      const options = Array.from(optionsEl.children);
      options.sort(compareChipOrder);
      for (const option of options) optionsEl.appendChild(option);
    }
  }

  #customChipFor(position, type) {
    return (
      this.#chipsFor(position).find(
        (c) => c.dataset.custom === 'true' && c.dataset.swatchType === type
      ) || null
    );
  }

  #isCustomDialogOpen() {
    return Boolean(this.#customDialog?.querySelector('dialog')?.open);
  }

  #customOptionByHandle(handle) {
    if (!this.#customDialog || !handle) return null;
    return this.#customDialog.querySelector(
      `[data-custom-option][data-handle="${CSS.escape(handle)}"]`
    );
  }

  #customTypeName(type) {
    const authored = this.#customLabels.types?.[type]?.name;
    if (authored) return authored;
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
  }

  // Slot labels for (type, count): authored per-type list from the labels
  // blob (Tip/Middle/Base, Primary/Spots, …), else "Colour {n}".
  #customSlotLabels(type, count) {
    const authored = this.#customLabels.types?.[type]?.slots?.[String(count)];
    if (Array.isArray(authored) && authored.length === count) return authored;
    const template = this.#customLabels.slotFallback || 'Colour {n}';
    return Array.from({ length: count }, (_, i) => template.replace('{n}', String(i + 1)));
  }

  #openCustomDialog(position, type) {
    if (!this.#customDialog || !this.#positions[position] || !type) return;
    const chip = this.#customChipFor(position, type);
    if (!chip || chip.dataset.disabled === 'true') return;

    const costOptions = (chip.dataset.costOptions || '')
      .split(',')
      .map(Number)
      .filter((n) => Number.isInteger(n) && n >= 2);
    if (costOptions.length === 0) return;

    // Prefill from a previously-applied combo on this chip.
    const prevHandles = (chip.dataset.customHandles || '').split('+').filter(Boolean);
    const count = costOptions.includes(prevHandles.length) ? prevHandles.length : costOptions[0];
    const slots = Array.from({ length: count }, (_, i) => {
      const option = this.#customOptionByHandle(prevHandles[i]);
      if (!option) return null;
      return { handle: prevHandles[i], color: option.dataset.color, label: option.dataset.label };
    });
    const firstEmpty = slots.findIndex((s) => !s);

    this.#customState = {
      position,
      type,
      count,
      costOptions,
      slots,
      active: firstEmpty === -1 ? 0 : firstEmpty,
      // The mode the builder opened in — Apply restores it if something
      // (variant morph, gating) shifted the picker while the dialog was up.
      mode: this.#mode,
    };
    this.#renderCustomDialog();
    this.#filterCustomOptions('');
    this.#customDialog.showDialog?.();
  }

  // Bound at init — BEFORE DialogComponent's own click listener (added per
  // showDialog), so stopImmediatePropagation here reliably pre-empts its
  // outside-click close. Per Sierra: the builder never light-dismisses —
  // only Apply / Cancel / the X (and Esc) close it.
  #onCustomDialogClick(event) {
    const target = event.target;
    // Backdrop and padding/gap clicks target the <dialog> element itself —
    // swallow them so they can't dismiss the builder.
    if (target instanceof HTMLDialogElement) {
      event.stopImmediatePropagation();
      return;
    }
    if (!(target instanceof Element)) return;
    const countButton = target.closest('[data-custom-count]');
    const slotButton = countButton ? null : target.closest('[data-custom-slot]');
    const filterButton = slotButton ? null : target.closest('[data-filter-category]');
    const option = filterButton ? null : target.closest('[data-custom-option]');
    const apply = option ? null : target.closest('[data-custom-apply]');
    if (!countButton && !slotButton && !filterButton && !option && !apply) return;
    // Handled control click — DialogComponent's outside-check must not
    // re-evaluate it (a re-rendered node would read as "outside").
    event.stopImmediatePropagation();
    if (countButton) return this.#setCustomCount(Number(countButton.dataset.customCount));
    if (slotButton) return this.#setActiveCustomSlot(Number(slotButton.dataset.customSlot));
    if (filterButton) return this.#filterCustomOptions(filterButton.dataset.filterCategory || '');
    if (option) return this.#assignCustomOption(option);
    this.#applyCustomSelection();
  }

  #renderCustomDialog() {
    const state = this.#customState;
    const dialog = this.#customDialog;
    if (!state || !dialog) return;

    const title = dialog.querySelector('[data-custom-title]');
    if (title) {
      const template = title.dataset.titleTemplate || '{type}';
      title.textContent = template.replace('{type}', this.#customTypeName(state.type));
    }

    const countRow = dialog.querySelector('[data-custom-count-row]');
    if (countRow) {
      countRow.hidden = state.costOptions.length < 2;
      for (const button of countRow.querySelectorAll('[data-custom-count]')) {
        const n = Number(button.dataset.customCount);
        button.hidden = !state.costOptions.includes(n);
        button.setAttribute('aria-pressed', n === state.count ? 'true' : 'false');
      }
    }

    // Build slot buttons only when the count changes; update in place
    // otherwise. Replacing a just-clicked slot button would detach it
    // mid-bubble (DialogComponent's contains() check reads that as an
    // outside click) and would drop keyboard focus every interaction.
    const slotsEl = dialog.querySelector('[data-custom-slots]');
    if (slotsEl) {
      const labels = this.#customSlotLabels(state.type, state.count);
      if (slotsEl.children.length !== state.count) {
        slotsEl.replaceChildren(
          ...state.slots.map((_, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'swatch-custom__slot';
            button.dataset.customSlot = String(i);
            const preview = document.createElement('span');
            preview.className = 'swatch-custom__slot-preview';
            const label = document.createElement('span');
            label.className = 'swatch-custom__slot-label';
            const value = document.createElement('span');
            value.className = 'swatch-custom__slot-value';
            button.append(preview, label, value);
            return button;
          })
        );
      }
      Array.from(slotsEl.children).forEach((button, i) => {
        const slot = state.slots[i];
        button.setAttribute('aria-pressed', i === state.active ? 'true' : 'false');
        const preview = button.querySelector('.swatch-custom__slot-preview');
        if (preview) {
          if (slot) {
            preview.style.setProperty('--swatch-color', slot.color);
            delete preview.dataset.empty;
          } else {
            preview.style.removeProperty('--swatch-color');
            preview.dataset.empty = 'true';
          }
        }
        const label = button.querySelector('.swatch-custom__slot-label');
        if (label) label.textContent = labels[i] || '';
        const value = button.querySelector('.swatch-custom__slot-value');
        if (value) value.textContent = slot ? slot.label : this.#customLabels.emptySlot || '';
      });
    }

    const picked = new Set(state.slots.filter(Boolean).map((s) => s.handle));
    for (const option of dialog.querySelectorAll('[data-custom-option]')) {
      option.setAttribute('aria-pressed', picked.has(option.dataset.handle) ? 'true' : 'false');
    }

    const apply = dialog.querySelector('[data-custom-apply]');
    if (apply) apply.disabled = state.slots.some((s) => !s);
  }

  #setActiveCustomSlot(index) {
    const state = this.#customState;
    if (!state || !Number.isInteger(index) || index < 0 || index >= state.count) return;
    state.active = index;
    this.#renderCustomDialog();
  }

  // Remap picks across a count change: first and last stay put (Tip/Base),
  // middles carry over in order while they fit.
  #setCustomCount(count) {
    const state = this.#customState;
    if (!state || !state.costOptions.includes(count) || count === state.count) return;
    const old = state.slots;
    const next = Array.from({ length: count }, () => null);
    next[0] = old[0] ?? null;
    next[count - 1] = old[old.length - 1] ?? null;
    const middles = old.slice(1, -1).filter(Boolean);
    let mi = 0;
    for (let i = 1; i < count - 1 && mi < middles.length; i++) next[i] = middles[mi++];
    state.count = count;
    state.slots = next;
    const firstEmpty = next.findIndex((s) => !s);
    state.active = firstEmpty === -1 ? 0 : firstEmpty;
    this.#renderCustomDialog();
  }

  // Assign to the active slot; a colour already in another slot MOVES here
  // (keeps every slot distinct without dead-end error states), then advance
  // to the next empty slot so a straight run of taps fills the combo.
  #assignCustomOption(option) {
    const state = this.#customState;
    if (!state) return;
    const handle = option.dataset.handle;
    if (!handle) return;
    for (let i = 0; i < state.slots.length; i++) {
      if (i !== state.active && state.slots[i]?.handle === handle) state.slots[i] = null;
    }
    state.slots[state.active] = {
      handle,
      color: option.dataset.color,
      label: option.dataset.label,
    };
    const nextEmpty = state.slots.findIndex((s) => !s);
    if (nextEmpty !== -1) state.active = nextEmpty;
    this.#renderCustomDialog();
  }

  #filterCustomOptions(category) {
    const dialog = this.#customDialog;
    if (!dialog) return;
    for (const pill of dialog.querySelectorAll('[data-filter-category]')) {
      pill.setAttribute(
        'aria-pressed',
        (pill.dataset.filterCategory || '') === category ? 'true' : 'false'
      );
    }
    for (const option of dialog.querySelectorAll('[data-custom-option]')) {
      option.hidden = Boolean(category) && option.dataset.category !== category;
    }
  }

  #applyCustomSelection() {
    const state = this.#customState;
    if (!state || state.slots.some((s) => !s)) return;
    const chip = this.#customChipFor(state.position, state.type);
    if (!chip) return;

    // Re-assert the mode the builder opened in before selecting — if the
    // picker shifted while the dialog was up, applying into the stale mode
    // would write this surface's properties alongside the other mode's
    // picks (both would ride to cart). #applyMode also clears the other
    // mode's selections, so no stale properties leak.
    if (state.mode && this.#mode !== state.mode && this.#modeRegions[state.mode]?.length) {
      this.#applyMode(state.mode);
    }

    const handles = state.slots.map((s) => s.handle);
    const labels = this.#customSlotLabels(state.type, state.count);
    // Faded is directional (tip → base); other types are compositions.
    const sep = state.type === 'faded' ? ' → ' : ' + ';
    // Customer-facing: colour names only. The slot-labelled breakdown with
    // hex codes goes in the hidden `_{Surface} Pour` property for admin.
    const label = `${this.#customLabels.strip || 'Custom'} ${this.#customTypeName(state.type)}: ${state.slots
      .map((slot) => slot.label)
      .join(sep)}`;
    const pour = state.slots
      .map((slot, i) => `${labels[i]}: ${slot.label} ${(slot.color || '').toUpperCase()}`)
      .join(sep);

    chip.dataset.swatchId = `custom:${state.type}:${handles.join('+')}`;
    chip.dataset.customHandles = handles.join('+');
    // Rides the standard handle-property pipeline; cart-products parses the
    // custom: prefix and resolves each solid's live colour for the
    // composite thumbnail.
    chip.dataset.swatchHandle = `custom:${state.type}:${handles.join('+')}`;
    chip.dataset.customPour = pour;
    chip.dataset.colorCost = String(state.count);
    chip.dataset.color = state.slots[0].color;
    chip.dataset.color2 = state.slots[state.count - 1].color;
    if (state.count > 2) chip.dataset.colorMid = state.slots[1].color;
    else delete chip.dataset.colorMid;
    chip.dataset.label = label;
    chip.setAttribute('aria-label', label);
    this.#paintCustomChipVisual(chip, state);

    this.#customDialog?.closeDialog?.();
    this.#customState = null;
    // Internal re-entry: select through the normal path, don't reopen.
    this.#applyingCustom = true;
    try {
      chip.click();
    } finally {
      this.#applyingCustom = false;
    }
  }

  #paintCustomChipVisual(chip, state) {
    const visual = chip.querySelector('.swatch-chip__visual');
    if (!visual) return;
    visual.style.setProperty('--swatch-color', state.slots[0].color);
    visual.style.setProperty('--swatch-color-2', state.slots[state.count - 1].color);
    if (state.count > 2) visual.style.setProperty('--swatch-color-mid', state.slots[1].color);
    else visual.style.removeProperty('--swatch-color-mid');
    if (state.type === 'faded') {
      const inner = document.createElement('span');
      inner.className =
        state.count > 2 ? 'swatch-chip__faded swatch-chip__faded--tri' : 'swatch-chip__faded';
      visual.replaceChildren(inner);
      return;
    }
    // Organic pattern SVG (single source: swatch-pattern-svg.liquid, cloned
    // from the inert <template>s beside the builder dialog). Splotch has
    // its own shape set; any other multi-colour type shares the mottled
    // spot language. Tinting rides the vars set on the visual above.
    const patternKey = state.type === 'splotch' ? 'splotch' : 'mottled';
    const template = this.querySelector(`template[data-custom-pattern="${patternKey}"]`);
    const svg = template?.content?.firstElementChild;
    if (svg) {
      visual.replaceChildren(svg.cloneNode(true));
      return;
    }
    // Partial-deploy fallback: the CSS dot approximation.
    const inner = document.createElement('span');
    inner.className =
      state.type === 'splotch'
        ? 'swatch-chip__custom-pattern swatch-chip__custom-pattern--splotch'
        : 'swatch-chip__custom-pattern';
    visual.replaceChildren(inner);
  }

  #sortChips() {
    // Perceptual secondary sort by [cost, hue, sat, lightness] — Liquid
    // can pre-sort by color_cost but has no hex→HSL filter.
    for (const container of this.querySelectorAll('.swatch-picker__chips')) {
      const chips = Array.from(container.children);
      chips.sort(compareChipOrder);
      for (const chip of chips) container.appendChild(chip);
    }
  }

}

if (!customElements.get('swatch-picker')) {
  customElements.define('swatch-picker', SwatchPicker);
}

/**
 * Headless paint driver for Premade PDPs (no swatch picker). Reads the
 * variant→signature_combo map from a JSON blob and dispatches the same
 * bunny:swatch-change events the picker would — at mount and on every
 * variant:update.
 *
 * JSON shape (N-up): each variant entry has a `surfaces` array of
 * {name, id, type, color, color2, label, cost, sparkle}. All same id =
 * Model/Entire paint; else one split event per surface.
 *
 * Listens for variant:update directly because Horizon's morph preserves
 * DOM identity — connectedCallback won't re-fire.
 */
class BunnyPremadePaint extends HTMLElement {
  #variants = {};
  #currentId = null;
  #onVariantUpdateBound = null;
  // Panel scope so sibling panels' variant:update doesn't adopt their
  // variant id as our own and emit a stale paint.
  #hostPanel = null;

  connectedCallback() {
    loadPourCostMap();
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
      try {
        const parsed = JSON.parse(script.textContent || '{}');
        this.#variants = parsed.variants || {};
      } catch (err) {
        console.warn(
          '[bunny-premade-paint] failed to parse variants JSON:', err,
          { host: this, initialVariant: this.dataset.initialVariant, snippet: (script.textContent || '').slice(0, 120) }
        );
        this.#variants = {};
      }
      this.#validateVariants();
    }
    this.#currentId = this.dataset.initialVariant || null;
    this.#hostPanel = this.closest('[data-pour-panel]');

    requestAnimationFrame(() => this.#emit(this.#currentId));

    this.#onVariantUpdateBound = (event) => this.#onVariantUpdate(event);
    document.addEventListener('variant:update', this.#onVariantUpdateBound);
  }

  disconnectedCallback() {
    if (this.#onVariantUpdateBound) {
      document.removeEventListener('variant:update', this.#onVariantUpdateBound);
      this.#onVariantUpdateBound = null;
    }
  }

  // Re-emit when a late-loading 3D viewer's #hydrateFromPicker calls in.
  replayCurrent() {
    this.#emit(this.#currentId);
  }

  // Console warn on missing JSON keys so Liquid typos surface in dev tools.
  // #emit already early-returns on missing fields.
  #validateVariants() {
    const requiredColor = ['name', 'id', 'type', 'color', 'cost'];
    for (const [variantId, entry] of Object.entries(this.#variants)) {
      const surfaces = entry?.surfaces;
      if (!Array.isArray(surfaces) || surfaces.length === 0) {
        console.warn(`[bunny-premade-paint] variant ${variantId} missing surfaces array`);
        continue;
      }
      for (const surface of surfaces) {
        const missing = requiredColor.filter((k) => surface == null || surface[k] == null);
        if (missing.length) {
          console.warn(`[bunny-premade-paint] variant ${variantId} surface ${surface?.name ?? '?'} missing keys: ${missing.join(', ')}`);
        }
      }
    }
  }

  #onVariantUpdate(event) {
    if (this.#hostPanel && event.target?.closest?.('[data-pour-panel]') !== this.#hostPanel) return;
    const newId = event?.detail?.resource?.id;
    if (newId == null) return;
    const idStr = String(newId);
    if (idStr === this.#currentId) return;
    this.#currentId = idStr;
    this.#emit(idStr);
  }

  #emit(variantId) {
    if (!variantId) return;
    const entry = this.#variants[variantId];
    if (!entry) return;
    const surfaces = entry.surfaces;
    if (!Array.isArray(surfaces) || surfaces.length === 0) return;

    // All same id = Entire Model paint; else one split event per surface.
    // Mirrors selectSignature so the viewer doesn't special-case premades.
    const firstId = surfaces[0].id;
    const allSame = surfaces.every((s) => s.id === firstId);
    if (allSame) {
      this.#dispatch('model', 'entire', surfaces[0]);
    } else {
      for (const surface of surfaces) {
        this.#dispatch(surface.name, 'split', surface);
      }
    }

    this.#emitRarity(surfaces);
  }

  #dispatch(region, mode, swatch) {
    this.dispatchEvent(new CustomEvent('bunny:swatch-change', {
      bubbles: true,
      composed: true,
      detail: { region, mode, swatch },
    }));
  }

  // Same shape as SwatchPicker.#totalCost: sum every surface, Monolithic
  // shortcut when all share a swatch. Dispatches bunny:rarity-change.
  #emitRarity(surfaces) {
    const firstId = surfaces[0]?.id;
    const allSame = firstId != null && surfaces.every((s) => s.id === firstId);
    let total = 0;
    if (allSame) {
      total = Number(surfaces[0].cost) || 0;
    } else {
      for (const s of surfaces) total += Number(s.cost) || 0;
    }
    this.dispatchEvent(new CustomEvent('bunny:rarity-change', {
      bubbles: true,
      composed: true,
      detail: {
        cost: total,
        rarity: COST_TO_RARITY[total] || null,
        pourStyle: COST_TO_POUR_STYLE[total] || null,
      },
    }));
  }
}

if (!customElements.get('bunny-premade-paint')) {
  customElements.define('bunny-premade-paint', BunnyPremadePaint);
}
